import React from 'react'
import { Link } from 'react-router-dom'
import { useDashboardStats, useCases } from '../../hooks/useCases'
import {
  FolderIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlusCircleIcon,
  TruckIcon,
} from '@heroicons/react/24/outline'
import { formatDate, formatCurrency, formatStatus } from '../../utils/formatters'
import { getStatusColor } from '../../utils/helpers'
import LoadingSpinner from '../../components/LoadingSpinner'

function DashboardPage() {
  const { data: stats, isLoading: statsLoading } = useDashboardStats()
  const { data: casesData, isLoading: casesLoading } = useCases(1, 5)

  if (statsLoading || casesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  const statCards = [
    {
      name: 'Total Cases',
      value: stats?.totalCases || 0,
      icon: FolderIcon,
      color: 'bg-blue-500',
    },
    {
      name: 'Active Cases',
      value: stats?.activeCases || 0,
      icon: ClockIcon,
      color: 'bg-yellow-500',
    },
    {
      name: 'Completed',
      value: stats?.completedCases || 0,
      icon: CheckCircleIcon,
      color: 'bg-green-500',
    },
    {
      name: 'Pending Documents',
      value: stats?.pendingDocuments || 0,
      icon: ExclamationTriangleIcon,
      color: 'bg-red-500',
    },
  ]

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Dashboard</h1>
            <p className="mt-1 text-sm text-neutral-600">
              Welcome to your funeral services coordination dashboard
            </p>
          </div>
          {/* Case feature on hold — creation disabled */}
          <button
            type="button"
            disabled
            aria-disabled="true"
            title="Case feature coming soon"
            className="btn-primary flex items-center gap-2 opacity-50 cursor-not-allowed"
          >
            <PlusCircleIcon className="h-5 w-5" />
            New Case
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => {
          const Icon = stat.icon
          return (
            <div key={stat.name} className="card">
              <div className="card-body p-6">
                <div className="flex items-center">
                  <div className={`flex-shrink-0 p-3 rounded-lg ${stat.color}`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-neutral-600 truncate">
                        {stat.name}
                      </dt>
                      <dd className="text-lg font-semibold text-neutral-900">
                        {stat.value.toLocaleString()}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Recent Cases */}
      <div className="mt-8">
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-neutral-900">Recent Cases</h3>
            <p className="text-sm text-neutral-500">Latest funeral cases you're managing</p>
          </div>
          <div className="card-body">
            {casesData?.data.length === 0 ? (
              <div className="text-center py-12">
                <FolderIcon className="mx-auto h-12 w-12 text-neutral-400" />
                <h3 className="mt-2 text-sm font-medium text-neutral-900">No cases yet</h3>
                <p className="mt-1 text-sm text-neutral-500">
                  Get started by creating your first funeral case.
                </p>
                <div className="mt-6">
                  <button
                    type="button"
                    disabled
                    aria-disabled="true"
                    title="Case feature coming soon"
                    className="btn-primary opacity-50 cursor-not-allowed"
                  >
                    Create New Case
                  </button>
                </div>
              </div>
            ) : (
              <div className="overflow-hidden">
                <table className="min-w-full divide-y divide-neutral-200">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Case Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Deceased Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="relative px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-neutral-200">
                    {casesData?.data.map((case_) => (
                      <tr key={case_.id} className="hover:bg-neutral-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">
                          {case_.caseNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                          {case_.deceasedName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(case_.status)}`}>
                            {formatStatus(case_.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                          {formatDate(case_.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Link
                            to={`/cases/${case_.caseNumber}`}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            View Details
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {casesData && casesData.data.length > 0 && (
              <div className="card-footer">
                <div className="flex justify-between">
                  <p className="text-sm text-neutral-700">
                    Showing {casesData.data.length} of {casesData.total} results
                  </p>
                  <Link
                    to="/cases"
                    className="text-sm font-medium text-primary-600 hover:text-primary-500"
                  >
                    View all cases <span aria-hidden="true">&rarr;</span>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card opacity-50 pointer-events-none">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <PlusCircleIcon className="h-8 w-8 text-neutral-400" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-neutral-900">Create New Case</h3>
                <p className="text-sm text-neutral-500">Start coordinating funeral services</p>
              </div>
            </div>
            <div className="mt-4">
              <button
                disabled
                className="btn-primary w-full cursor-not-allowed"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-neutral-900">View Active Cases</h3>
                <p className="text-sm text-neutral-500">Manage ongoing funeral arrangements</p>
              </div>
            </div>
            <div className="mt-4">
              <Link
                to="/cases?status=active"
                className="btn-outline w-full"
              >
                View Cases
              </Link>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FolderIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-neutral-900">Browse Providers</h3>
                <p className="text-sm text-neutral-500">Find verified service providers</p>
              </div>
            </div>
            <div className="mt-4">
              <Link
                to="/providers"
                className="btn-outline w-full"
              >
                Browse Now
              </Link>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TruckIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-neutral-900">Manage Bookings</h3>
                <p className="text-sm text-neutral-500">Complete pending ambulance payments</p>
              </div>
            </div>
            <div className="mt-4">
              <Link
                to="/bookings"
                className="btn-outline w-full"
              >
                Manage Bookings
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}


export default DashboardPage